'use client'
import { AddToCalendarButton, TrackedAddToCalButtonNames } from '@components/add-to-calendar'
import { AllPurposeLink } from '@components/all-purpose-link'
import { formatUpcomingDate } from '@utils/common'
import { ValidStatusType } from '@utils/project'
import { projectShowRoute } from '@utils/routes'
import { DetailText, HeadlineText, CalendarPlusIcon } from 'design-configuration/components'
import { motion } from 'motion/react'
import { exhibitionTransition } from './header'
import { Button } from '@components/button'
import Accordion from '@components/accordion'
import { MoreDetailsBlock } from './works'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { useSoloViewer } from '@components/solo-viewer/provider'
import { ShopifyProduct } from '@utils/shopify'
import { ShopifyPurchaseButton } from './shopify-purchase'
import { ExhibitionCollectionProjectFragment } from '@/gql/generated/graphql'
import { getPhysicalProjectEvent } from '../utils/exhibition-utils'

export const WorksPhysicalReleaseCard = ({
  project,
  title,
  description,
  image,
  moreDetailsBlocks,
  shopifyProduct,
  exhibitionSlug
}: {
  project: ExhibitionCollectionProjectFragment
  title: string
  description: React.ReactElement
  image: string
  moreDetailsBlocks: { title: string; description: React.ReactElement }[]
  shopifyProduct: ShopifyProduct
  exhibitionSlug: string
}): React.ReactElement => {
  const { setSoloViewerImageData } = useSoloViewer()
  const startTime = new Date(project.start_datetime ?? '')
  let status = ValidStatusType.upcoming
  if (startTime < new Date()) {
    status = ValidStatusType.live
  }
  if (project.invocations === project.max_invocations) {
    status = ValidStatusType.complete
  }

  return (
    <motion.div
      className="border-outline-variant flex h-fit w-full flex-grow flex-col justify-between border"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={exhibitionTransition}
    >
      <div className="flex flex-col">
        <button
          className="relative aspect-[3/2] w-full"
          onClick={() => {
            setSoloViewerImageData({ image, imageList: [image] })
          }}
        >
          <ImageWithCustomLoader
            src={image}
            alt="Physical release"
            style={{
              objectFit: 'cover'
            }}
            sizes="50vw"
            fill
          />
        </button>
        <div className="p-md">
          <div className="flex items-center justify-between">
            <div>
              <DetailText className="text-on-surface-variant font-medium uppercase">
                {formatUpcomingDate(startTime)}
              </DetailText>
              <HeadlineText className="mt-2xs font-light">{title}</HeadlineText>
            </div>
            {status === ValidStatusType.upcoming ? (
              <AddToCalendarButton
                events={[getPhysicalProjectEvent(project, exhibitionSlug)]}
                colorTheme={'inverted-secondary'}
                className="border-outline-variant p-xs px-xs hover:border-outline border"
                buttonName={TrackedAddToCalButtonNames.exhibitionWorksCard}
              >
                <CalendarPlusIcon className="w-md h-md fill-on-surface" />
              </AddToCalendarButton>
            ) : null}
          </div>
          {description}
          <Accordion title="More details" titleClassName="text-on-surface-variant underline fill-on-surface-variant">
            <div className="gap-y-xs flex flex-col">
              {moreDetailsBlocks.map((block, index) => (
                <MoreDetailsBlock key={index} title={block.title} description={block.description} />
              ))}
            </div>
          </Accordion>
        </div>
      </div>
      <div className="mt-sm px-md pb-md w-full">
        {status === ValidStatusType.upcoming ? (
          <Button colorTheme={'primary'} branded disabled className="w-full">
            Starting soon
          </Button>
        ) : status === ValidStatusType.live ? (
          <ShopifyPurchaseButton
            shopifyProduct={shopifyProduct}
            colorTheme={'primary'}
            branded
            className="h-full w-full"
          />
        ) : status === ValidStatusType.complete ? (
          <AllPurposeLink href={projectShowRoute(project?.slug ?? '')} className="w-full">
            <Button colorTheme={'secondary'} branded className="mt-sm w-full bg-transparent">
              View the digital collection
            </Button>
          </AllPurposeLink>
        ) : null}
      </div>
    </motion.div>
  )
}
